<template>
  <base-dialog @cancel="cancelDialog" :width="520">
    <div slot="header">企业实名认证</div>
    <div slot="body">
      <a-form :form="form.fc">
        <a-form-item label="营业执照" v-bind="formItemLayout" extra="请上传清晰的企业营业执照照片（jpg/png，不超过4MB）">
          <a-upload
            listType="picture-card"
            :fileList="fileList"
            :beforeUpload="beforeUpload"
            :remove="handleRemove"
            @preview="handlePreview"
            @change="handleFileChange">
            <div v-if="fileList.length < 1">
              <a-icon :type="uploading ? 'loading' : 'plus'" />
              <div class="ant-upload-text">{{ uploading ? '识别中' : '上传' }}</div>
            </div>
          </a-upload>
          <a-modal :visible="previewVisible" :footer="null" @cancel="previewVisible = false">
            <img alt="营业执照" style="width: 100%" :src="previewImage" />
          </a-modal>
        </a-form-item>
        <a-form-item label="企业名称" v-bind="formItemLayout">
          <a-input v-decorator="decorators.name" placeholder="上传营业执照后自动识别" disabled :loading="loading || uploading" />
        </a-form-item>
        <a-form-item label="统一信用代码" v-bind="formItemLayout">
          <a-input v-decorator="decorators.license_number" placeholder="上传营业执照后自动识别" disabled :loading="loading || uploading" />
        </a-form-item>
      </a-form>
    </div>
    <div slot="footer">
      <a-button type="primary" @click="handleConfirm" :loading="loading || uploading" :disabled="!recognized">实名认证</a-button>
      <a-button @click="cancelDialog">{{ $t('dialog.cancel') }}</a-button>
    </div>
  </base-dialog>
</template>

<script>
import { mapState } from 'vuex'
import DialogMixin from '@/mixins/dialog'
import WindowsMixin from '@/mixins/windows'
import { uuid } from '@/utils/utils'

function getBase64 (file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = error => reject(error)
  })
}

export default {
  name: 'EditEnterpriseNameDialog',
  mixins: [DialogMixin, WindowsMixin],
  data () {
    return {
      loading: false,
      uploading: false,
      recognized: false,
      fileList: [],
      previewVisible: false,
      previewImage: '',
      form: {
        fc: this.$form.createForm(this),
      },
      formItemLayout: {
        wrapperCol: {
          span: 16,
        },
        labelCol: {
          span: 7,
        },
      },
      decorators: {
        name: [
          'name',
          {
            rules: [
              { required: true, message: '请先上传营业执照以识别企业名称' },
            ],
          },
        ],
        license_number: [
          'license_number',
          {
            rules: [
              { required: true, message: '请先上传营业执照以识别统一信用代码' },
            ],
          },
        ],
      },
      company_type: '',
      registered_address: '',
      legal_person: '',
      registered_capital: '',
      registered_date: '',
      issue_date: '',
    }
  },
  computed: {
    ...mapState('app', {
      customerId: state => (state.customerCapability || {}).customer_id,
    }),
  },
  methods: {
    beforeUpload (file) {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
      if (!isJpgOrPng) {
        this.$message.error('仅支持 jpg/png 格式图片')
        return false
      }
      const isLt4M = file.size / 1024 / 1024 < 4
      if (!isLt4M) {
        this.$message.error('图片大小不能超过 4MB')
        return false
      }
      this.uploadLicense(file)
      return false
    },
    handleFileChange ({ fileList, file }) {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
      if (!isJpgOrPng) return
      if (file.size && file.size / 1024 / 1024 >= 4) return
      this.fileList = fileList.slice(-1)
    },
    handleRemove () {
      this.fileList = []
      this.recognized = false
      this.form.fc.setFieldsValue({
        name: undefined,
        license_number: undefined,
      })
      return true
    },
    async handlePreview (file) {
      if (!file.url && !file.preview) {
        file.preview = await getBase64(file.originFileObj || file)
      }
      this.previewImage = file.url || file.preview
      this.previewVisible = true
    },
    handleUpload (file) {
      return this.$http({
        url: '/v2/enterprise-attachments',
        method: 'post',
        processData: false,
        data: file,
        headers: {
          'X-Enterprise-Meta-Type': 'credentials',
          'X-Enterprise-Meta-FileName': encodeURI(file.name),
          'X-Enterprise-Meta-CustomerID': this.customerId,
          'Content-Type': 'application/octet-stream',
        },
        timeout: 0,
        params: {
          $t: uuid(),
        },
      })
    },
    async uploadLicense (file) {
      this.uploading = true
      this.recognized = false
      try {
        const preview = await getBase64(file)
        this.fileList = [{
          uid: file.uid || `-${Date.now()}`,
          name: file.name,
          status: 'done',
          url: preview,
          originFileObj: file,
        }]
        const { data } = await this.handleUpload(file)
        const name = data.company_name || ''
        const license_number = data.credit_code || ''
        this.company_type = data.company_type || ''
        this.registered_address = data.business_address || ''
        this.legal_person = data.legal_person || ''
        this.registered_capital = data.registered_capital || ''
        this.registered_date = data.registration_date || ''
        this.issue_date = data.issue_date || ''
        if (!name || !license_number) {
          this.$message.error('未能识别企业名称或统一信用代码，请重新上传清晰的营业执照')
          return
        }
        this.form.fc.setFieldsValue({ name, license_number })
        this.recognized = true
      } catch (error) {
        this.fileList = []
        this.form.fc.setFieldsValue({
          name: undefined,
          license_number: undefined,
        })
      } finally {
        this.uploading = false
      }
    },
    doUpdate (data) {
      return new this.$Manager('customers').performAction({
        id: this.customerId,
        action: 'enterprise-info',
        data,
      })
    },
    async handleConfirm () {
      this.loading = true
      try {
        const { name, license_number } = await this.form.fc.validateFields()
        await this.doUpdate({
          name,
          license_number,
          company_type: this.company_type,
          reg_address: this.registered_address,
          legal_person: this.legal_person,
          registered_capital: this.registered_capital,
          registered_date: this.registered_date,
          issue_date: this.issue_date,
        })
        this.loading = false
        this.cancelDialog()
        this.$message.success('企业实名认证成功')
        this.$store.dispatch('app/getEnterPriseInfo')
        this.$store.dispatch('app/getCustomerCapabilities')
      } catch (error) {
        this.loading = false
      }
    },
  },
}
</script>
